import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type ReconItem } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtCurrency } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, ReconStatusPill } from "@/components/dash/ui";

const STATUSES = ["all", "matched", "conflict", "possible_match", "insufficient_data", "not_applicable"];

function reviewTypeFor(it: ReconItem): string {
  if (it.status === "conflict") return "reconciliation_conflict";
  if (it.double_counting_risk) return "double_counting_risk";
  if (it.source_b === "mart.customer" && it.source_a === "mart.customer") return "suspicious_duplicate_customer";
  if (it.source_b === "mart.customer") return "high_value_unmatched_customer";
  return "reconciliation_conflict";
}

function RaiseButton({ it }: { it: ReconItem }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => dashApi.createReview({
      dedup_key: `recon:${it.reference ?? it.source_a}:${it.status}`,
      review_type: reviewTypeFor(it),
      severity: it.status === "conflict" ? "warning" : "info",
      title: `${it.source_a} vs ${it.source_b}`,
      detail: it.explanation,
      source_code: null,
      entity_ref: it.reference,
      confidence: it.confidence,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trust-reviews"] }),
  });
  return (
    <button type="button" onClick={() => m.mutate()} disabled={m.isPending || m.isSuccess}
      className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-[11px] text-brand-200 hover:bg-brand-500/20 disabled:opacity-50">
      {m.isSuccess ? "Raised ✓" : m.isPending ? "…" : "Raise for review"}
    </button>
  );
}

export default function ReconciliationCentrePage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const q = useQuery({ queryKey: ["reconciliation"], queryFn: dashApi.reconciliation });
  const [status, setStatus] = useState("all");
  const items = (q.data?.items ?? []).filter((i) => status === "all" || i.status === status);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reconciliation Centre"
        description="Cross-system consistency checks. Only source-backed figures are compared; where the data has no link to match on, the status is honestly 'insufficient data' — never a fabricated match. Possible matches are never auto-merged and never write to a source."
        right={
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
          </select>
        }
      />
      <Panel title={`Reconciliation checks (${items.length})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !items.length ? (
          <EmptyState>No reconciliation results for this filter.</EmptyState>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="pb-2 font-medium">Source A</th><th className="pb-2 font-medium">Source B</th>
              <th className="pb-2 text-right font-medium">A</th><th className="pb-2 text-right font-medium">B</th>
              <th className="pb-2 text-right font-medium">Diff</th><th className="pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium">Explanation</th><th className="pb-2" />
            </tr></thead>
            <tbody className="divide-y divide-ink-500/40">
              {items.map((it, idx) => (
                <tr key={idx} className="text-slate-300 align-top">
                  <td className="py-2 text-slate-200">{it.source_a}</td>
                  <td className="py-2 text-slate-200">{it.source_b}</td>
                  <td className="py-2 text-right tabular-nums">{it.amount_a === null ? "—" : fmtCurrency(it.amount_a)}</td>
                  <td className="py-2 text-right tabular-nums">{it.amount_b === null ? "—" : fmtCurrency(it.amount_b)}</td>
                  <td className="py-2 text-right tabular-nums">{it.financial_difference === null ? "—" : fmtCurrency(it.financial_difference)}</td>
                  <td className="py-2">
                    <ReconStatusPill status={it.status} />
                    {it.double_counting_risk && <div className="mt-0.5 text-[10px] text-amber-300">⚠ double-count risk</div>}
                  </td>
                  <td className="py-2 text-xs text-slate-500 max-w-md">{it.explanation}</td>
                  <td className="py-2 text-right">
                    {isAdmin && (it.status === "conflict" || it.status === "possible_match" || it.double_counting_risk) && <RaiseButton it={it} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
